import { useCallback, useLayoutEffect, useRef } from 'react'

export function useHeaderOffset() {
    // stable refs for observers + scheduling
    const roRef = useRef<ResizeObserver | null>(null)
    const moRef = useRef<MutationObserver | null>(null)
    // Guards against queueing more than one pending measurement at a time.
    const isScheduledRef = useRef<boolean>(false)
    // Track which elements the ResizeObserver is already bound to so we only
    // call observe() once per element instance.
    const observedTabsRef = useRef<Element | null>(null)
    const observedNavbarRef = useRef<Element | null>(null)

    const observeOnce = useCallback(
        (el: Element | null, ref: React.MutableRefObject<Element | null>) => {
            if (!el || el === ref.current || !roRef.current) return
            try {
                roRef.current.observe(el)
                ref.current = el
            } catch {
                // ignored
            }
        },
        [],
    )

    const getEls = () => ({
        navbar: document.querySelector(
            '.navbar-container',
        ) as HTMLElement | null,
        tabs: document.querySelector('.mpm-page-tabs') as HTMLElement | null,
        // Measure only the tablist bar, not the entire Tabs component
        // (which includes tab content panels that can be thousands of px tall)
        tablist: document.querySelector(
            '.mpm-page-tabs [role="tablist"]',
        ) as HTMLElement | null,
        main: document.querySelector(
            '.main-content-container',
        ) as HTMLElement | null,
    })

    const measure = useCallback(() => {
        const { navbar, tablist } = getEls()
        const navbarH = navbar ? navbar.getBoundingClientRect().height : 0
        const tabsH = tablist ? tablist.getBoundingClientRect().height : 0
        const totalHeader = navbarH + tabsH
        return {
            navbarH,
            totalHeader,
        }
    }, [])

    const apply = useCallback(
        ({
            navbarH,
            totalHeader,
        }: {
            navbarH: number
            totalHeader: number
        }) => {
            const { navbar, tabs, main } = getEls()

            // Attach the ResizeObserver lazily. None of these elements are
            // guaranteed to exist when the layout effect first runs — Canvas's
            // Layout mounts .main-content-container asynchronously, and
            // PageTabs renders inside child route components — so binding to
            // them once at mount silently produced observers watching nothing.
            observeOnce(navbar, observedNavbarRef)
            observeOnce(tabs, observedTabsRef)

            // .mpm-page-tabs is itself position:sticky (see index.scss).
            // Set its top inline to the exact navbar height so it sticks
            // precisely at the bottom edge of the fixed navbar.
            // The CSS fallback is top: var(--header-height, 0px) which
            // is correct once --header-height is written below.
            if (tabs) {
                const desired = `${navbarH}px`
                if (tabs.style.top !== desired) {
                    tabs.style.top = desired
                }
            }

            // main-content-container only needs padding for the fixed
            // navbar. The sticky .mpm-page-tabs sits in-flow below this
            // padding and sticks on scroll — no extra padding needed.
            if (main) {
                const desired = `${navbarH}px`
                if (main.style.paddingTop !== desired) {
                    main.style.paddingTop = desired
                }
            }

            // Set CSS custom property for total header height (navbar + tabs).
            // .mpm-time-periods--fixed positions itself from this value, so if
            // it is never written the time period bar renders at top: 0 and
            // disappears behind the navbar.
            document.documentElement.style.setProperty(
                '--header-height',
                `${totalHeader}px`,
            )
        },
        [observeOnce],
    )

    // Coalesce bursts of observer callbacks into a single measurement.
    //
    // This deliberately does not use requestAnimationFrame. The previous
    // implementation scheduled the write in a rAF and cancelled the pending
    // frame on every observer callback, so a continuous stream of DOM
    // mutations inside .main-content-container (Recharts re-rendering its SVG
    // each frame, React Query refetches, placeholder swaps) could starve it
    // indefinitely and --header-height would never be written at all. rAF is
    // also throttled outright in backgrounded or occluded pages.
    //
    // A microtask always runs, cannot be starved by further mutations, and
    // still coalesces a burst of callbacks into one measurement per task.
    const calculateOffset = useCallback(() => {
        if (isScheduledRef.current) return
        isScheduledRef.current = true

        queueMicrotask(() => {
            isScheduledRef.current = false
            apply(measure())
        })
    }, [measure, apply])

    useLayoutEffect(() => {
        // create observers if missing
        if (!roRef.current) {
            roRef.current = new ResizeObserver(() => calculateOffset())
        }
        if (!moRef.current) {
            moRef.current = new MutationObserver(() => calculateOffset())
        }

        // Initial measurement runs synchronously, before the browser paints,
        // so --header-height is always set for the very first frame and the
        // CSS fallback never has to be load-bearing.
        apply(measure())

        // Observe the app root rather than the individual header elements.
        //
        // .navbar-container, .mpm-page-tabs and .main-content-container are all
        // mounted asynchronously — Canvas's Layout renders the body after
        // MPMLayout's layout effect fires, and PageTabs renders inside child
        // route components. Querying them here returned null, so the observer
        // was attached to nothing at all: --header-height stayed at the
        // navbar-only height and the fixed time period bar was positioned
        // underneath the page tabs, where users could not see or tap it.
        //
        // #root is present for the lifetime of the app, so this catches those
        // elements whenever they appear, remount, or change between routes.
        const appRoot = document.getElementById('root') ?? document.body

        try {
            moRef.current!.observe(appRoot, {
                childList: true,
                subtree: true,
            })
        } catch {
            // ignored
        }

        // Orientation changes can swap the viewport between the portrait and
        // landscape layouts without necessarily resizing an observed element,
        // so re-measure explicitly. The fixed time period bar is positioned
        // from --header-height and would otherwise keep a stale offset.
        window.addEventListener('resize', calculateOffset)
        window.addEventListener('orientationchange', calculateOffset)

        return () => {
            window.removeEventListener('resize', calculateOffset)
            window.removeEventListener('orientationchange', calculateOffset)

            roRef.current?.disconnect()
            moRef.current?.disconnect()
            roRef.current = null
            moRef.current = null
            observedTabsRef.current = null
            observedNavbarRef.current = null
            isScheduledRef.current = false
        }
    }, [calculateOffset, apply, measure, observeOnce])
}
